import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

const CapacityForecastChart = ({ onClick }) => {
  const data = {
    labels: ['Q1 2023', 'Q2 2023', 'Q3 2023', 'Q4 2023', 'Q1 2024F', 'Q2 2024F', 'Q3 2024F', 'Q4 2024F'],
    datasets: [
      {
        label: 'Capacity Utilization %',
        data: [78, 82, 85, 87, 88, 90, 93, 95],
        borderColor: '#ff6384',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#ff6384',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0 && onClick) {
        const element = elements[0];
        const index = element.index;
        const value = data.datasets[0].data[index];
        const period = data.labels[index];
        const isForecast = period.includes('F') || period.includes('2024');
        
        onClick({
          period,
          value,
          index,
          isForecast,
          isCritical: value >= 95,
          status: value >= 95 ? 'Critical' : value >= 90 ? 'High' : value >= 85 ? 'Medium' : 'Normal'
        });
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Utilization %'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value) {
            return value + '%';
          },
          stepSize: 10
        }
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value}%`;
          },
          afterLabel: function(context) {
            const value = context.parsed.y;
            let status = '';
            let color = '';
            
            if (value >= 95) {
              status = 'Critical - Capacity nearly full';
              color = '#dc3545';
            } else if (value >= 90) {
              status = 'High - Monitor closely';
              color = '#ffc107';
            } else if (value >= 85) {
              status = 'Medium - Optimal range';
              color = '#28a745';
            } else {
              status = 'Normal - Healthy capacity';
              color = '#007bff';
            }
            
            return `Status: ${status}`;
          }
        }
      },
      annotation: {
        annotations: {
          criticalLine: {
            type: 'line',
            yMin: 95,
            yMax: 95,
            borderColor: '#ff6384',
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              content: 'Critical Level: 95%',
              enabled: true,
              position: 'end',
              backgroundColor: '#ff6384',
              color: '#ffffff',
              font: {
                size: 12,
                weight: 'bold'
              },
              padding: {
                x: 10,
                y: 5
              },
              borderRadius: 4
            }
          },
          highZone: {
            type: 'box',
            yMin: 90,
            yMax: 95,
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            borderColor: 'rgba(255, 193, 7, 0.3)',
            borderWidth: 1,
            label: {
              content: 'High',
              enabled: true,
              position: 'center',
              backgroundColor: 'rgba(255, 193, 7, 0.8)',
              color: '#212529',
              font: {
                size: 10
              }
            }
          },
          optimalZone: {
            type: 'box',
            yMin: 80,
            yMax: 90,
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            borderColor: 'rgba(40, 167, 69, 0.3)',
            borderWidth: 1,
            label: {
              content: 'Optimal',
              enabled: true,
              position: 'center',
              backgroundColor: 'rgba(40, 167, 69, 0.8)',
              color: '#ffffff',
              font: {
                size: 10
              }
            }
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    elements: {
      line: {
        tension: 0.4
      }
    }
  };

  return <Line data={data} options={options} />;
};

export default CapacityForecastChart;